/**
 * Formulário de Cadastro/Edição de Empresa (Tenant)
 * Componente reutilizável para criar e editar empresas
 * 
 * @author Equipe GestaoeCRM
 * @date 29/09/2025
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  CreditCard,
  Users,
  FolderOpen,
  ClipboardList,
  X,
  Save,
  Clock,
  Plus,
  Minus
} from 'lucide-react';
import { useTenant, Tenant } from '@/contexts/TenantContext';
import { useAuth } from '@/contexts/AuthContext';
import { trialApi } from '@/services/api';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';

// Schema de validação
const tenantSchema = z.object({
  cnpj: z.string()
    .min(14, 'CNPJ deve ter 14 dígitos')
    .max(18, 'CNPJ deve ter formato válido')
    .regex(/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/, 'CNPJ deve estar no formato 00.000.000/0000-00'),
  companyName: z.string()
    .min(2, 'Razão social deve ter pelo menos 2 caracteres')
    .max(100, 'Razão social deve ter no máximo 100 caracteres'),
  tradeName: z.string()
    .max(100, 'Nome fantasia deve ter no máximo 100 caracteres')
    .optional(),
  email: z.string()
    .email('Email deve ter formato válido')
    .max(100, 'Email deve ter no máximo 100 caracteres'),
  phone: z.string()
    .regex(/^\(\d{2}\)\s\d{4,5}-\d{4}$/, 'Telefone deve estar no formato (00) 00000-0000')
    .optional(),
  addressStreet: z.string()
    .max(100, 'Rua deve ter no máximo 100 caracteres')
    .optional(),
  addressNumber: z.string()
    .max(10, 'Número deve ter no máximo 10 caracteres')
    .optional(),
  addressComplement: z.string()
    .max(50, 'Complemento deve ter no máximo 50 caracteres')
    .optional(),
  addressNeighborhood: z.string()
    .max(50, 'Bairro deve ter no máximo 50 caracteres')
    .optional(),
  addressCity: z.string()
    .max(50, 'Cidade deve ter no máximo 50 caracteres')
    .optional(),
  addressState: z.string()
    .length(2, 'Estado deve ter 2 caracteres')
    .optional(),
  addressZipcode: z.string()
    .regex(/^\d{5}-\d{3}$/, 'CEP deve estar no formato 00000-000')
    .optional(),
  planType: z.enum(['FREE', 'BASIC', 'PROFESSIONAL', 'ENTERPRISE']),
  maxUsers: z.number()
    .int('Número de usuários deve ser inteiro')
    .min(1, 'Mínimo 1 usuário')
    .max(1000, 'Máximo 1000 usuários'),
  maxProjects: z.number()
    .int('Número de projetos deve ser inteiro')
    .min(1, 'Mínimo 1 projeto')
    .max(10000, 'Máximo 10000 projetos'),
  maxDemandsPerMonth: z.number()
    .int('Número de demandas deve ser inteiro')
    .min(1, 'Mínimo 1 demanda por mês')
    .max(100000, 'Máximo 100000 demandas por mês')
});

type TenantFormData = z.infer<typeof tenantSchema>;

interface TenantFormProps {
  tenant?: Tenant | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function TenantForm({ tenant, isOpen, onClose, onSuccess }: TenantFormProps) {
  const { createTenant, updateTenant, loading } = useTenant();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [trialDaysInput, setTrialDaysInput] = useState<number>(1);
  const [isUpdatingTrial, setIsUpdatingTrial] = useState(false);
  
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  // Buscar status do trial quando tenant estiver disponível
  const { data: trialStatus, refetch: refetchTrialStatus } = useQuery({
    queryKey: ['trial-status', tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return null;
      const response = await trialApi.getTrialStatus(tenant.id);
      return response.data.success ? response.data.data : null;
    },
    enabled: !!tenant?.id && isOpen,
    refetchInterval: 60000, // Atualizar a cada minuto
  });

  // Atualizar input quando trialStatus mudar
  useEffect(() => {
    if (trialStatus?.trialEndsAt) {
      const now = new Date();
      const trialEndsAt = new Date(trialStatus.trialEndsAt);
      const diff = trialEndsAt.getTime() - now.getTime();
      const daysRemaining = Math.ceil(diff / (1000 * 60 * 60 * 24));
      setTrialDaysInput(Math.max(1, daysRemaining));
    }
  }, [trialStatus]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<TenantFormData>({
    resolver: zodResolver(tenantSchema),
    defaultValues: {
      planType: 'BASIC',
      maxUsers: 5,
      maxProjects: 10,
      maxDemandsPerMonth: 50
    }
  });

  const planType = watch('planType');

  // Atualizar valores padrão baseado no plano
  useEffect(() => {
    switch (planType) {
      case 'FREE':
        setValue('maxUsers', 3);
        setValue('maxProjects', 5);
        setValue('maxDemandsPerMonth', 25);
        break;
      case 'BASIC':
        setValue('maxUsers', 5);
        setValue('maxProjects', 10);
        setValue('maxDemandsPerMonth', 50);
        break;
      case 'PROFESSIONAL':
        setValue('maxUsers', 20);
        setValue('maxProjects', 50);
        setValue('maxDemandsPerMonth', 200);
        break;
      case 'ENTERPRISE':
        setValue('maxUsers', 100);
        setValue('maxProjects', 500);
        setValue('maxDemandsPerMonth', 1000);
        break;
    }
  }, [planType, setValue]);

  // Resetar formulário quando modal abrir/fechar
  useEffect(() => {
    if (isOpen) {
      if (tenant) {
        // Modo edição
        reset({
          cnpj: tenant.cnpj,
          companyName: tenant.companyName,
          tradeName: tenant.tradeName || '',
          email: tenant.email,
          phone: tenant.phone || '',
          addressStreet: tenant.addressStreet || '',
          addressNumber: tenant.addressNumber || '',
          addressComplement: tenant.addressComplement || '',
          addressNeighborhood: tenant.addressNeighborhood || '',
          addressCity: tenant.addressCity || '',
          addressState: tenant.addressState || '',
          addressZipcode: tenant.addressZipcode || '',
          planType: tenant.planType,
          maxUsers: tenant.maxUsers,
          maxProjects: tenant.maxProjects,
          maxDemandsPerMonth: tenant.maxDemandsPerMonth
        });
        // Atualizar trialDaysInput
        if (tenant.trialDays) {
          setTrialDaysInput(tenant.trialDays);
        } else if (trialStatus?.trialDays) {
          setTrialDaysInput(trialStatus.trialDays);
        } else {
          setTrialDaysInput(60);
        }
      } else {
        // Modo criação
        reset({
          cnpj: '',
          companyName: '',
          tradeName: '',
          email: '',
          phone: '',
          addressStreet: '',
          addressNumber: '',
          addressComplement: '',
          addressNeighborhood: '',
          addressCity: '',
          addressState: '',
          addressZipcode: '',
          planType: 'BASIC',
          maxUsers: 5,
          maxProjects: 10,
          maxDemandsPerMonth: 50
        });
        setTrialDaysInput(60);
      }
    }
  }, [isOpen, tenant, reset, trialStatus]);

  // Função para formatar CNPJ
  const formatCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  // Função para formatar telefone
  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    } else {
      return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    }
  };

  // Função para formatar CEP
  const formatCEP = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/^(\d{5})(\d{3})$/, '$1-$2');
  };

  // Função para formatar estado (2 letras maiúsculas)
  const formatState = (value: string) => {
    return value.toUpperCase().slice(0, 2);
  };

  // Calcular dias restantes do trial
  const calculateDaysRemaining = (): number => {
    if (!trialStatus?.trialEndsAt) return 0;
    const now = new Date();
    const trialEndsAt = new Date(trialStatus.trialEndsAt);
    const diff = trialEndsAt.getTime() - now.getTime();
    const daysRemaining = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return daysRemaining > 0 ? daysRemaining : 0;
  };

  // Atualizar dias de trial - calcula diferença e adiciona/subtrai
  const handleUpdateTrialDays = async () => {
    if (!tenant?.id || !isSuperAdmin || !trialStatus) return;
    
    try {
      setIsUpdatingTrial(true);
      
      // Calcular dias restantes atuais
      const now = new Date();
      const trialEndsAt = new Date(trialStatus.trialEndsAt);
      const daysRemaining = Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      // Calcular diferença entre o que o usuário quer e o que tem
      const difference = trialDaysInput - daysRemaining;
      
      if (difference === 0) {
        toast.info('Nenhuma alteração necessária');
        return;
      }
      
      // Usar extendTrial para adicionar ou subtrair a diferença
      const response = await trialApi.extendTrial(
        tenant.id, 
        difference, 
        difference > 0 ? 'Ajuste manual pelo super admin (adicionar)' : 'Ajuste manual pelo super admin (subtrair)'
      );
      
      if (response.data.success) {
        toast.success('Dias de trial atualizados com sucesso!');
        await refetchTrialStatus();
        onSuccess?.();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao atualizar dias de trial');
    } finally {
      setIsUpdatingTrial(false);
    }
  };

  // Estender ou reduzir trial
  const handleExtendTrial = async (additionalDays: number) => {
    if (!tenant?.id || !isSuperAdmin) return;
    
    try {
      setIsUpdatingTrial(true);
      const response = await trialApi.extendTrial(tenant.id, additionalDays, additionalDays > 0 ? 'Extensão manual pelo super admin' : 'Redução manual pelo super admin');
      
      if (response.data.success) {
        const action = additionalDays > 0 ? 'adicionados' : 'subtraídos';
        const daysText = Math.abs(additionalDays) === 1 ? 'dia' : 'dias';
        toast.success(`${Math.abs(additionalDays)} ${daysText} ${action} ao trial com sucesso!`);
        await refetchTrialStatus();
        onSuccess?.();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao modificar trial');
    } finally {
      setIsUpdatingTrial(false);
    }
  };

  const daysRemaining = calculateDaysRemaining();
  const isTrialActive = trialStatus?.isTrialActive ?? false;

  const onSubmit = async (data: TenantFormData) => {
    try {
      setIsSubmitting(true);

      if (tenant) {
        // Modo edição
        await updateTenant(tenant.id, data);
        toast.success('Empresa atualizada com sucesso!');
      } else {
        // Modo criação
        await createTenant(data);
        toast.success('Empresa criada com sucesso!');
      }

      onSuccess?.();
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar empresa');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-glass rounded-button bg-background-glass backdrop-blur-glass">
        <div className="mt-3">
          <h3 className="text-lg font-bold text-text-primary mb-4">
            {tenant ? 'Editar Empresa' : 'Nova Empresa'}
          </h3>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Informações Básicas */}
            <div>
              <h4 className="text-md font-bold text-text-primary mb-4 flex items-center">
                <Building2 className="h-5 w-5 mr-2 text-secondary" />
                Informações Básicas
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-text-secondary">
                    CNPJ *
                  </label>
                  <input
                    {...register('cnpj')}
                    type="text"
                    placeholder="00.000.000/0000-00"
                    className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                    onChange={(e) => {
                      const formatted = formatCNPJ(e.target.value);
                      setValue('cnpj', formatted);
                    }}
                  />
                  {errors.cnpj && (
                    <p className="mt-1 text-sm text-red-400">{errors.cnpj.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-text-secondary">
                    Razão Social *
                  </label>
                  <input
                    {...register('companyName')}
                    type="text"
                    placeholder="Nome da empresa"
                    className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                  />
                  {errors.companyName && (
                    <p className="mt-1 text-sm text-red-400">{errors.companyName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-text-secondary">
                    Nome Fantasia
                  </label>
                  <input
                    {...register('tradeName')}
                    type="text"
                    placeholder="Nome comercial (opcional)"
                    className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                  />
                  {errors.tradeName && (
                    <p className="mt-1 text-sm text-red-400">{errors.tradeName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-text-secondary">
                    Email *
                  </label>
                  <input
                    {...register('email')}
                    type="email"
                    placeholder="contato@empresa.com"
                    className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-text-secondary">
                    Telefone
                  </label>
                  <input
                    {...register('phone')}
                    type="text"
                    placeholder="(00) 00000-0000"
                    className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                    onChange={(e) => {
                      const formatted = formatPhone(e.target.value);
                      setValue('phone', formatted);
                    }}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-400">{errors.phone.message}</p>
                  )}
                </div>
              </div>
            </div>

          {/* Endereço */}
          <div>
            <h4 className="text-md font-bold text-text-primary mb-4 flex items-center">
              <MapPin className="h-5 w-5 mr-2 text-secondary" />
              Endereço
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-text-secondary">
                  Rua
                </label>
                <input
                  {...register('addressStreet')}
                  type="text"
                  placeholder="Nome da rua"
                  className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                />
                {errors.addressStreet && (
                  <p className="mt-1 text-sm text-red-400">{errors.addressStreet.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-text-secondary">
                  Número
                </label>
                <input
                  {...register('addressNumber')}
                  type="text"
                  placeholder="123"
                  className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                />
                {errors.addressNumber && (
                  <p className="mt-1 text-sm text-red-400">{errors.addressNumber.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-text-secondary">
                  Complemento
                </label>
                <input
                  {...register('addressComplement')}
                  type="text"
                  placeholder="Sala 101"
                  className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                />
                {errors.addressComplement && (
                  <p className="mt-1 text-sm text-red-400">{errors.addressComplement.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-text-secondary">
                  Bairro
                </label>
                <input
                  {...register('addressNeighborhood')}
                  type="text"
                  placeholder="Centro"
                  className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                />
                {errors.addressNeighborhood && (
                  <p className="mt-1 text-sm text-red-400">{errors.addressNeighborhood.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-text-secondary">
                  Cidade
                </label>
                <input
                  {...register('addressCity')}
                  type="text"
                  placeholder="São Paulo"
                  className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                />
                {errors.addressCity && (
                  <p className="mt-1 text-sm text-red-400">{errors.addressCity.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-text-secondary">
                  Estado
                </label>
                <input
                  {...register('addressState')}
                  type="text"
                  placeholder="SP"
                  maxLength={2}
                  className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                  onChange={(e) => {
                    const formatted = formatState(e.target.value);
                    setValue('addressState', formatted);
                  }}
                />
                {errors.addressState && (
                  <p className="mt-1 text-sm text-red-400">{errors.addressState.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-text-secondary">
                  CEP
                </label>
                <input
                  {...register('addressZipcode')}
                  type="text"
                  placeholder="00000-000"
                  className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                  onChange={(e) => {
                    const formatted = formatCEP(e.target.value);
                    setValue('addressZipcode', formatted);
                  }}
                />
                {errors.addressZipcode && (
                  <p className="mt-1 text-sm text-red-400">{errors.addressZipcode.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Controle de Trial */}
          {tenant && (
            <div>
              <h4 className="text-md font-bold text-text-primary mb-4 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-secondary" />
                Período de Trial
              </h4>
              <div className="bg-background-glass/50 border border-border-glass rounded-button p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-text-secondary mb-2">
                      Status do Trial
                    </label>
                    <div className="flex items-center space-x-3">
                      <div className={`px-3 py-2 rounded-button text-sm font-bold ${
                        isTrialActive 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {isTrialActive ? 'Trial Ativo' : 'Trial Expirado'}
                      </div>
                      {isTrialActive && (
                        <div className="text-text-primary">
                          <span className="font-bold text-lg">{daysRemaining}</span>
                          <span className="text-sm ml-1">dias restantes</span>
                        </div>
                      )}
                    </div>
                    {trialStatus?.trialEndsAt && (
                      <p className="text-xs text-text-muted mt-2">
                        Término: {new Date(trialStatus.trialEndsAt).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                  </div>
                  
                  {isSuperAdmin && (
                    <div>
                      <label className="block text-sm font-bold text-text-secondary mb-2">
                        Dias de Trial
                      </label>
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => setTrialDaysInput(Math.max(1, trialDaysInput - 1))}
                          className="px-2 py-1 border border-border-glass rounded-button shadow-glass bg-background-glass hover:bg-background-glass-hover text-text-primary"
                          disabled={isUpdatingTrial}
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={trialDaysInput}
                          onChange={(e) => setTrialDaysInput(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))}
                          className="w-20 text-center border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary"
                          disabled={isUpdatingTrial}
                        />
                        <button
                          type="button"
                          onClick={() => setTrialDaysInput(Math.min(365, trialDaysInput + 1))}
                          className="px-2 py-1 border border-border-glass rounded-button shadow-glass bg-background-glass hover:bg-background-glass-hover text-text-primary"
                          disabled={isUpdatingTrial}
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={handleUpdateTrialDays}
                          disabled={isUpdatingTrial}
                          className="px-3 py-1 text-xs font-bold text-white bg-gradient-to-r from-primary to-secondary hover:brightness-110 rounded-button shadow-glass disabled:opacity-50"
                        >
                          {isUpdatingTrial ? '...' : 'Atualizar'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {isSuperAdmin && (
                  <div className="flex items-center space-x-2 pt-2 border-t border-border-glass/30 flex-wrap gap-2">
                    <span className="text-sm text-text-secondary">Ações rápidas:</span>
                    <button
                      type="button"
                      onClick={() => handleExtendTrial(-30)}
                      disabled={isUpdatingTrial}
                      className="px-3 py-1 text-xs font-bold text-text-primary border border-border-glass rounded-button shadow-glass bg-background-glass hover:bg-background-glass-hover disabled:opacity-50"
                    >
                      -30 dias
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExtendTrial(-15)}
                      disabled={isUpdatingTrial}
                      className="px-3 py-1 text-xs font-bold text-text-primary border border-border-glass rounded-button shadow-glass bg-background-glass hover:bg-background-glass-hover disabled:opacity-50"
                    >
                      -15 dias
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExtendTrial(-7)}
                      disabled={isUpdatingTrial}
                      className="px-3 py-1 text-xs font-bold text-text-primary border border-border-glass rounded-button shadow-glass bg-background-glass hover:bg-background-glass-hover disabled:opacity-50"
                    >
                      -7 dias
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExtendTrial(7)}
                      disabled={isUpdatingTrial}
                      className="px-3 py-1 text-xs font-bold text-text-primary border border-border-glass rounded-button shadow-glass bg-background-glass hover:bg-background-glass-hover disabled:opacity-50"
                    >
                      +7 dias
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExtendTrial(15)}
                      disabled={isUpdatingTrial}
                      className="px-3 py-1 text-xs font-bold text-text-primary border border-border-glass rounded-button shadow-glass bg-background-glass hover:bg-background-glass-hover disabled:opacity-50"
                    >
                      +15 dias
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExtendTrial(30)}
                      disabled={isUpdatingTrial}
                      className="px-3 py-1 text-xs font-bold text-text-primary border border-border-glass rounded-button shadow-glass bg-background-glass hover:bg-background-glass-hover disabled:opacity-50"
                    >
                      +30 dias
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Plano e Limites */}
          <div>
            <h4 className="text-md font-bold text-text-primary mb-4 flex items-center">
              <CreditCard className="h-5 w-5 mr-2 text-secondary" />
              Plano e Limites
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-text-secondary">
                  Tipo de Plano *
                </label>
                <select
                  {...register('planType')}
                  className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="FREE">Grátis - R$ 0/mês</option>
                  <option value="BASIC">Básico - R$ 97/mês</option>
                  <option value="PROFESSIONAL">Profissional - R$ 197/mês</option>
                  <option value="ENTERPRISE">Empresarial - R$ 397/mês</option>
                </select>
                {errors.planType && (
                  <p className="mt-1 text-sm text-red-400">{errors.planType.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-text-secondary">
                  Máximo de Usuários *
                </label>
                <input
                  {...register('maxUsers', { valueAsNumber: true })}
                  type="number"
                  min="1"
                  max="1000"
                  className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                />
                {errors.maxUsers && (
                  <p className="mt-1 text-sm text-red-400">{errors.maxUsers.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-text-secondary">
                  Máximo de Projetos *
                </label>
                <input
                  {...register('maxProjects', { valueAsNumber: true })}
                  type="number"
                  min="1"
                  max="10000"
                  className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                />
                {errors.maxProjects && (
                  <p className="mt-1 text-sm text-red-400">{errors.maxProjects.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-text-secondary">
                  Máximo de Demandas por Mês *
                </label>
                <input
                  {...register('maxDemandsPerMonth', { valueAsNumber: true })}
                  type="number"
                  min="1"
                  max="100000"
                  className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                />
                {errors.maxDemandsPerMonth && (
                  <p className="mt-1 text-sm text-red-400">{errors.maxDemandsPerMonth.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-border-glass/30">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-button shadow-glass text-sm font-bold text-white bg-gradient-to-r from-primary to-secondary hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {tenant ? 'Atualizar' : 'Criar'}
                </>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}
